import { environment } from '@environments/environment';
import { AppRoutesEnum } from '@shared/types';

export interface AppNavigation {
    id: string;
    name: string;
    url: string;
    internalRoute?: boolean;
    submenu?: AppNavigation[];
}

const accountsSubmenu: AppNavigation[] = [
    {
        id: 'accounts',
        name: 'Accounts',
        url: environment.omAccountsUrl,
    },
    {
        id: 'accounts-organizations',
        name: 'Organizations',
        url: environment.omAccountsOrganizationsUrl,
    },
    {
        id: 'accounts-online-users',
        name: 'Online Users',
        url: environment.omAccountsOnlineUsersUrl,
    },
];

const assaysSubmenu: AppNavigation[] = [
    {
        id: 'assays-dashboard',
        name: 'Dashboard',
        url: environment.omAssaysUrl,
    },
    {
        id: 'assays-listing',
        name: 'Assay Listing',
        internalRoute: true,
        url: AppRoutesEnum.ASSAY_LISTING,
    },
    {
        id: 'assays-sequencing',
        name: 'Sequencing',
        internalRoute: true,
        url: AppRoutesEnum.ASSAY_SEQUENCING,
    },
    {
        id: 'assays-probe-mfg',
        name: 'Probe Mfg.',
        internalRoute: true,
        url: AppRoutesEnum.ASSAY_PROBE_MANUFACTURING,
    },
    {
        id: 'assays-constructs',
        name: 'Constructs',
        internalRoute: true,
        url: AppRoutesEnum.ASSAY_CONSTRUCTS,
    },
];

const ordersSubmenu: AppNavigation[] = [
    {
        id: 'orders-order-search',
        name: 'Order Search',
        url: environment.omOrdersUrl,
    },
    {
        id: 'orders-create-orders',
        name: 'Create/Modify Order',
        url: environment.omOrdersCreateUrl,
    },
    {
        id: 'orders-pending-orders',
        name: 'Pending Orders',
        url: environment.omOrdersPendingOrdersUrl,
    },
    {
        id: 'orders-incomplete-orders',
        name: 'Incomplete Orders',
        url: environment.omOrdersIncompleteOrdersUrl,
    },
    {
        id: 'orders-wellplate-storage',
        name: 'Wellplate Storage',
        url: environment.omOrdersWellplateStorageUrl,
    },
];

const pricingSubmenu: AppNavigation[] = [
    {
        id: 'pricing-overview',
        name: 'Overview',
        url: environment.omPricingUrl,
    },
    {
        id: 'pricing-promo-credits',
        name: 'Promo/Credits',
        url: environment.omPricingPromoCreditsUrl,
    },
    {
        id: 'pricing-payment-methods',
        name: 'Payment Methods',
        url: environment.omPricingPaymentMethodsUrl,
    },
];

const probesSubmenu: AppNavigation[] = [
    {
        id: 'probes-overview',
        name: 'Probe Listing',
        internalRoute: true,
        url: AppRoutesEnum.PROBE_LISTING,
    },
    {
        id: 'probes-abi-file-import',
        name: 'ABI File Import',
        internalRoute: true,
        url: AppRoutesEnum.PROBE_ABI_FILE_IMPORT,
    },
    {
        id: 'probes-manufacturing',
        name: 'Manufacturing',
        internalRoute: true,
        url: AppRoutesEnum.PROBE_MANUFACTURING,
    },
    {
        id: 'probes-incompatible-probes',
        name: 'Incompatible Probes',
        internalRoute: true,
        url: AppRoutesEnum.PROBE_INCOMPATIBLE,
    },
];

const pmSubmenu: AppNavigation[] = [
    {
        id: 'pm-overview',
        name: 'Overview',
        url: environment.omPmUrl,
    },
    {
        id: 'pm-tasks',
        name: 'Tasks',
        url: environment.omPmTasksUrl,
    },
    {
        id: 'pm-administration-notes',
        name: 'Notes',
        url: environment.omPmNotesUrl,
    },
    {
        id: 'pm-orders-lims',
        name: 'Orders by LIMS Status',
        url: environment.omPmOrdersLimsUrl,
    },
    {
        id: 'pm-pending-orders',
        name: 'Pending Orders',
        url: environment.omPmPendingOrdersUrl,
    },
    {
        id: 'pm-offline-wellplates',
        name: 'Offline Wellplates',
        url: environment.omPmOfflineWellplatesUrl,
    },
    {
        id: 'pm-offline-sequencing',
        name: 'Offline Sequencing',
        url: environment.omPmOfflineSequencingUrl,
    },
    {
        id: 'pm-strain-copy',
        name: 'Strain Copy',
        url: environment.omPmStrainCopyUrl,
    },
    {
        id: 'pm-unreceived-orders',
        name: 'Unreceived Orders',
        url: environment.omPmUnreceivedOrdersUrl,
    },
];

const salesSubmenu: AppNavigation[] = [
    {
        id: 'sales-dashboard',
        name: 'Dashboard',
        url: environment.omSalesUrl,
    },
    {
        id: 'sales-assay-design',
        name: 'Assay Design',
        url: environment.omSalesAssayDesignUrl,
    },
    {
        id: 'sales-administration-notes',
        name: 'Notes',
        url: environment.omSalesNotesUrl,
    },
    {
        id: 'sales-prospects',
        name: 'Prospects',
        url: environment.omSalesProspectsUrl,
    },
];

const shippingSubmenu: AppNavigation[] = [
    {
        id: 'shipping-supply-requests',
        name: 'Supply Requests',
        url: environment.omShippingUrl,
    },
    {
        id: 'shipping-central-shipping',
        name: 'Central Shipping',
        url: environment.omShippingCentralShippingUrl,
    },
    {
        id: 'shipping-sequencing',
        name: 'Sequencing',
        url: environment.omShippingSequencingUrl,
    },
    {
        id: 'shipping-label-settings',
        name: 'Label Settings',
        url: environment.omShippingLabelSettingsUrl,
    },
    {
        id: 'shipping-manage-pickups',
        name: 'Manage Pickups',
        url: environment.omManagePickupsUrl,
    },
];

const strainsSubmenu: AppNavigation[] = [
    {
        id: 'strains-strain-listing',
        name: 'Strain Listing',
        internalRoute: true,
        url: AppRoutesEnum.STRAIN_LISTING,
    },
    {
        id: 'strains-strain-copy',
        name: 'Strain Copy',
        internalRoute: true,
        url: AppRoutesEnum.STRAIN_COPY,
    },
];

const tagCenterSubmenu: AppNavigation[] = [
    {
        id: 'tag-center-contact-manager',
        name: 'Contact Manager',
        url: environment.omTagCenterUrl,
    },
];

const ckmSubmenu: AppNavigation[] = [
    {
        id: 'automated-genotyping-manager',
        name: 'Automated Genotyping',
        internalRoute: true,
        url: AppRoutesEnum.CKM_LISTING,
    },
    {
        id: 'sequencing-manager',
        name: 'Sequencing',
        internalRoute: true,
        url: AppRoutesEnum.SEQ_CKM_RETEST_LISTING,
    },
];

const seqSubmenu: AppNavigation[] = [
    {
        id: 'orders-management',
        name: 'Orders Management',
        internalRoute: true,
        url: AppRoutesEnum.SEQ_ORDERS_LIST,
    },
    {
        id: 'sample-inventory-management',
        name: 'Sample Inventory Management',
        internalRoute: true,
        url: AppRoutesEnum.SEQ_SAMPLE_MANAGEMENT,
    },
    {
        id: 'primer-manager',
        name: 'Primer Management',
        internalRoute: true,
        url: AppRoutesEnum.SEQ_PRIMER_MANAGEMENT,
    },
    {
        id: 'banner-management',
        name: 'Banner Management',
        internalRoute: true,
        url: AppRoutesEnum.SEQ_BANNER_MANAGEMENT,
    },
];

export const appNavigation: AppNavigation[] = [
    {
        id: 'dashboard', //0
        name: 'Dashboard',
        url: environment.omUrl,
    },
    {
        id: 'accounts', //1
        name: 'Accounts',
        url: environment.omAccountsUrl,
        submenu: accountsSubmenu,
    },
    {
        id: 'assays', //2
        name: 'Assays',
        url: AppRoutesEnum.ASSAYS,
        internalRoute: true,
        submenu: assaysSubmenu,
    },
    {
        id: 'orders', //3
        name: 'Orders',
        url: environment.omOrdersUrl,
        submenu: ordersSubmenu,
    },
    {
        id: 'pricing', //4
        name: 'Pricing',
        url: environment.omPricingUrl,
        submenu: pricingSubmenu,
    },
    {
        id: 'probes', //5
        name: 'Probes',
        url: AppRoutesEnum.PROBES,
        internalRoute: true,
        submenu: probesSubmenu,
    },
    {
        id: 'pm', //6
        name: 'PM',
        url: environment.omPmUrl,
        submenu: pmSubmenu,
    },
    {
        id: 'sales', //7
        name: 'Sales',
        url: environment.omSalesUrl,
        submenu: salesSubmenu,
    },
    {
        id: 'shipping', //8
        name: 'Shipping',
        url: environment.omShippingUrl,
        submenu: shippingSubmenu,
    },
    {
        id: 'ckm', //9
        name: 'CheckMate',
        url: 'checkmate',
        internalRoute: true,
        submenu: ckmSubmenu,
    },
    {
        id: 'seq', //10
        name: 'Sequencing',
        url: 'sequencing',
        internalRoute: true,
        submenu: seqSubmenu,
    },
    {
        id: 'strains', //11
        name: 'Strains',
        url: AppRoutesEnum.STRAINS,
        internalRoute: true,
        submenu: strainsSubmenu,
    },
    {
        id: 'tag-center', //12
        name: 'TAGCenter',
        url: environment.omTagCenterUrl,
        submenu: tagCenterSubmenu,
    },
];
